import React, { useCallback, useEffect, useState } from "react";
import { Activity, BadgeIndianRupee, PackageCheck, ShoppingBag, UserCheck, UserMinus, UserPlus, Users } from "lucide-react";
import { ReportsService } from "../../../services/reports.service";
import { ReportCard, ReportPanel } from "./components/ReportCards";
import DataTable from "./components/DataTable";
import InactiveList from "./components/InactiveList";
import TrendBars from "./components/TrendBars";
import UserDetailModal from "./components/UserDetailModal";
import { money, n } from "./components/reportFormat";

const rankingOptions = [
  ["paid", "Highest Paid"],
  ["orders", "Most Orders"],
  ["units", "Most Units"],
  ["aov", "Highest AOV"],
];

export default function UserReports({ filters }) {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState(null);
  const [inactive, setInactive] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [rankBy, setRankBy] = useState("paid");
  const [loading, setLoading] = useState(false);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userDetailError, setUserDetailError] = useState("");
  const [selectedUserRow, setSelectedUserRow] = useState(null);
  const [userOrdersPage, setUserOrdersPage] = useState(1);
  const userOrdersLimit = 10;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      ReportsService.userOverview(filters),
      ReportsService.users(filters),
      ReportsService.inactiveUsers(filters),
    ])
      .then(([a, b, c]) => {
        if (!alive) return;
        setOverview(a.data);
        setUsers(b.data);
        setInactive(c.data);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [filters]);

  useEffect(() => {
    let alive = true;
    setRankingLoading(true);
    ReportsService.userRanking({ ...filters, rankBy, page: 1, limit: 25 })
      .then((res) => alive && setRanking(res.data || null))
      .finally(() => alive && setRankingLoading(false));
    return () => { alive = false; };
  }, [filters, rankBy]);

  const fetchUserDetail = useCallback(async (row, page = 1) => {
    const userId = row?.user_id || row?.userId || row?.id;
    if (!userId) return;

    setSelectedUserRow(row);
    setUserOrdersPage(page);
    setUserDetailOpen(true);
    setUserDetailError("");
    setUserDetailLoading(true);

    try {
      const res = await ReportsService.userDetail(userId, {
        ...filters,
        ordersPage: page,
        ordersLimit: userOrdersLimit,
      });
      setUserDetail(res?.data || res || null);
    } catch (err) {
      setUserDetailError(err?.message || "User order history API failed");
    } finally {
      setUserDetailLoading(false);
    }
  }, [filters]);

  const openUserDetail = useCallback((row) => {
    setUserDetail(null);
    fetchUserDetail(row, 1);
  }, [fetchUserDetail]);

  const changeUserOrdersPage = useCallback((page) => {
    if (!selectedUserRow) return;
    fetchUserDetail(selectedUserRow, page);
  }, [fetchUserDetail, selectedUserRow]);

  const k = overview?.kpis || users?.stats || {};
  const rows = users?.rows || [];
  const rankingRows = ranking?.rows || [];
  const rankingStats = ranking?.stats || {};

  return (
    <div className="space-y-5">
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
        <ReportCard icon={Users} title="Total Users" value={n(k.total_users)} />
        <ReportCard icon={UserPlus} title="New Users" value={n(k.new_users)} />
        <ReportCard icon={UserCheck} title="Active Users" value={n(k.active_users)} />
        <ReportCard icon={ShoppingBag} title="Ordering Users" value={n(k.ordering_users)} />
        <ReportCard icon={BadgeIndianRupee} title="Paid by Customers" value={money(rankingStats.total_paid)} />
        <ReportCard icon={PackageCheck} title="Units Purchased" value={n(rankingStats.total_units)} />
      </div>

      <div className="rounded-[18px] border border-[#f4cdd2] bg-[#fffafa] p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-[17px] font-black text-slate-900">Customer Ranking</h2>
            <p className="mt-1 text-[11px] font-semibold text-slate-400">Ranking is calculated across all matching customers, not only the visible user page.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {rankingOptions.map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setRankBy(id)}
                className={`rounded-xl px-3 py-2 text-[11px] font-black transition ${rankBy === id ? "bg-[#d86a78] text-white" : "border border-[#f2c6cc] bg-white text-slate-500 hover:text-[#d86a78]"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          {rankingLoading ? <div className="py-12 text-center text-sm font-bold text-[#d86a78]">Calculating customer ranking...</div> : (
            <DataTable
              rows={rankingRows}
              columns={["rank", "user_name", "phone", "total_orders", "paid_orders", "total_paid", "total_units", "avg_order_value", "last_order_at"]}
              maxHeight="max-h-[440px]"
              onRowClick={openUserDetail}
            />
          )}
        </div>
      </div>

      {loading ? <div className="py-14 text-center font-bold text-[#d86a78]">Loading user report...</div> : (
        <>
          <div className="grid gap-5 xl:grid-cols-[1.1fr_1fr]">
            <TrendBars rows={overview?.timeline || []} field="new_users" label="New user timeline" />
            <ReportPanel title="User Activity KPIs">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-[#fff7f8] p-4"><div className="text-[11px] font-bold text-slate-400">Inactive Users</div><div className="mt-1 text-2xl font-black text-slate-900">{n(k.inactive_users)}</div></div>
                <div className="rounded-xl bg-[#fff7f8] p-4"><div className="text-[11px] font-bold text-slate-400">App Open Users</div><div className="mt-1 text-2xl font-black text-slate-900">{n(k.app_open_users)}</div></div>
                <div className="rounded-xl bg-[#fff7f8] p-4"><div className="text-[11px] font-bold text-slate-400">Orders in Ranking Scope</div><div className="mt-1 text-2xl font-black text-slate-900">{n(rankingStats.orders)}</div></div>
                <div className="rounded-xl bg-[#fff7f8] p-4"><div className="text-[11px] font-bold text-slate-400">Customers in Ranking</div><div className="mt-1 text-2xl font-black text-slate-900">{n(rankingStats.customers)}</div></div>
              </div>
            </ReportPanel>
          </div>

          <ReportPanel title="User Directory">
            <DataTable rows={rows} columns={["user_name","phone","user_status","user_created_at","last_login","total_orders","delivered_orders","total_spent","last_order_at"]} onRowClick={openUserDetail} />
          </ReportPanel>

          <InactiveList title="Inactive Users" rows={inactive?.rows || []} />
        </>
      )}
      <UserDetailModal
        open={userDetailOpen}
        loading={userDetailLoading}
        error={userDetailError}
        detail={userDetail}
        ordersPage={userOrdersPage}
        ordersLimit={userOrdersLimit}
        onOrdersPageChange={changeUserOrdersPage}
        onClose={() => {
          setUserDetailOpen(false);
          setUserDetail(null);
          setUserDetailError("");
          setSelectedUserRow(null);
          setUserOrdersPage(1);
        }}
      />
    </div>
  );
}
